import React, { useState, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import html2canvas from 'html2canvas';
import { Helmet } from 'react-helmet';
import { getDoc, doc } from 'firebase/firestore';
import { textdb } from '../../firebaseConfig';
import { FaDownload, FaWhatsapp, FaFileImage, FaCheckCircle, FaRedo, FaEdit, FaCut, FaShareAlt } from 'react-icons/fa';
import LazyLoad from 'react-lazy-load';
import { Link, useParams } from 'react-router-dom';
import { FaInstagram } from 'react-icons/fa';

// Import the CSS module for scoped styling
import styles from "./ImageShare.module.css"; 


export const Imageshare = () => {
  const { id } = useParams();

  // State for user interaction
  const [name, setName] = useState('');
  const [uploadedImage, setUploadedImage] = useState('');
  const [croppedImage, setCroppedImage] = useState(null);
  const [activeTab, setActiveTab] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [canvasImage, setCanvasImage] = useState(null); // This will hold the final base64 image data URL

  // State for data fetched from Firebase
  const [template, setTemplate] = useState(null);
  const [layout, setLayout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const finalImageRef = useRef(null);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const galleryDocRef = doc(textdb, 'galleries', id);
        const positionsDocRef = doc(textdb, 'positions', id);

        const [gallerySnap, positionsSnap] = await Promise.all([
            getDoc(galleryDocRef),
            getDoc(positionsDocRef)
        ]);

        if (gallerySnap.exists()) {
          setTemplate(gallerySnap.data());
        } else {
          console.error("Template not found!");
        }

        if (positionsSnap.exists()) {
          setLayout(positionsSnap.data());
        } else {
          console.error("Layout not found!");
        }
      } catch (error) {
          console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
    window.scrollTo(0, 0);
  }, [id]);

  // --- Event Handlers ---
  const handleNameChange = (event) => setName(event.target.value);
  const handleCreateClick = () => name.trim() !== '' && setActiveTab(2);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedImage(URL.createObjectURL(file));
      setActiveTab(3);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };
  
  const getCroppedImg = async (imageSrc, crop) => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; 
    image.src = imageSrc;
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
    return new Promise(resolve => canvas.toBlob(blob => resolve(URL.createObjectURL(blob)), 'image/png'));
  };

  const handleCropSubmit = async () => {
    if (!croppedAreaPixels) return;
    const croppedImg = await getCroppedImg(uploadedImage, croppedAreaPixels);
    setCroppedImage(croppedImg);
    setActiveTab(4); 
  };
  
  // Effect to generate the canvas once the cropped image is ready
  useEffect(() => {
    if (activeTab === 4 && croppedImage) {
      setTimeout(() => {
        if (finalImageRef.current) {
          html2canvas(finalImageRef.current, { 
              useCORS: true, 
              backgroundColor: null,
              scale: 2, // Use a fixed scale for consistent quality
              logging: false,
          }).then(canvas => {
            setCanvasImage(canvas.toDataURL('image/png', 1.0));
          });
        }
      }, 500);
    }
  }, [activeTab, croppedImage]);

  // --- FINAL ACTION HANDLERS (FIXED) ---

  const downloadImage = () => {
    if (!canvasImage) return;
    const link = document.createElement('a');
    link.href = canvasImage;
    link.download = `${name.replace(/\s+/g, '-')}-poster.png`;
    link.click();
  };
  
  /**
   * ROBUST GENERIC SHARE FUNCTION
   * This uses the Web Share API to share image, text, and link together.
   * THIS WILL ONLY WORK ON SUPPORTED MOBILE BROWSERS over HTTPS.
   */
  const handleGenericShare = async () => {
    // 1. Guard Clause: Check if the image is actually ready to be shared.
    if (!canvasImage || !template) {
      alert("Poster is not ready yet. Please wait a moment.");
      return;
    }

    try {
      // 2. Convert the base64 data URL to a Blob, then to a File object.
      // The Web Share API requires an array of File objects.
      const response = await fetch(canvasImage);
      const blob = await response.blob();
      const file = new File([blob], 'poster.png', { type: 'image/png' });

      // 3. Define all the data to be shared.
      const shareData = {
        files: [file],
        title: `Support ${template.Title1}`,
        text: `I just created this poster to support *${template.Title1}*! 🎉\nYou can create one too:`,
        url: window.location.href,
      };

      // 4. CRITICAL CHECK: Use navigator.canShare() to see if the browser can handle this data.
      // This is the most important step. It prevents errors on unsupported (e.g., desktop) browsers.
      if (navigator.share && navigator.canShare(shareData)) {
        // 5. If supported, call the API. This will open the native mobile share dialog.
        await navigator.share(shareData);
      } else {
        // 6. Fallback: If not supported, inform the user.
        alert("Your browser doesn't support direct sharing. Please download the image and share it manually.");
      }
    } catch (error) {
      // The user might cancel the share dialog, which throws an 'AbortError'. We can safely ignore it.
      if (error.name !== 'AbortError') {
        console.error("Sharing failed:", error);
        alert("Oh no! Something went wrong during the share attempt.");
      }
    }
  };

  /**
   * WHATSAPP SHARE FUNCTION
   * Note: This can ONLY share text and a link. WhatsApp does not allow pre-attaching images via a URL.
   * The user must attach the downloaded image manually.
   */
  const handleWhatsAppShare = () => {
    if (!template) return;
    const shareText = `I just created this poster to support *${template.Title1}*! 🎉\n\nI just downloaded my image. You can create one too by clicking the link below:\n`;
    const shareUrl = window.location.href;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + shareUrl)}`;
    window.open(whatsappUrl, '_blank');
  };

  // --- RENDER LOGIC ---

  if (isLoading || !template || !layout) {
    return (
        <div className={styles.pageContainer}>
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <h3>Loading Campaign...</h3>
            </div>
        </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Helmet>
        <title>{template.Title1 || 'Create your Poster'}</title>
        <meta name="description" content={`अपने पसंदीदा ${template.Title1} का समर्थन करने के लिए अपना खुद का व्यक्तिगत पोस्टर बनाएं।`} />

        <meta property="og:title" content={template.Title1} />
<meta property="og:description" content={`अपने पसंदीदा ${template.Title1} का समर्थन करने के लिए अपना खुद का व्यक्तिगत पोस्टर बनाएं।`} />

        <meta property="og:image" content={template.imageUrl} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>
      
      <div className={styles.shareCard}>
        {/* Step 1: Enter Name */}
        <div style={{ display: activeTab === 1 ? 'block' : 'none' }}>
            <LazyLoad><img className={styles.cardHeaderImage} style={{height:`${template.imageHeight}px`}} src={template.imageUrl} alt={template.Title1} /></LazyLoad>
            <div className={styles.cardBody}>
                <h3>Create your Personalised Poster</h3>
                <p style={{lineHeight:"0%" , fontWeight:"700"}}> For</p>
                <h4>{template.Title1}</h4>
                <input className={styles.nameInput} placeholder='Enter Your Name' type="text" value={name} onChange={handleNameChange} maxLength="14" />
                <button className={`${styles.actionButton} ${styles.primary}`} onClick={handleCreateClick} disabled={!name.trim()}>Create Poster</button>
            </div>
        </div>
        
        {/* Step 2: Upload Image */}
        <div style={{ display: activeTab === 2 ? 'block' : 'none' }}>
            <div className={styles.cardBody}>
                <h3>Welcome, {name}!</h3>
                <h4>Upload your profile photo</h4>
                <label htmlFor="file-input" className={`${styles.actionButton} ${styles.secondary}`}><FaFileImage /> Choose Image</label>
                <input id="file-input" type="file" className={styles.fileInput} accept="image/*" onChange={handleImageUpload} />
            </div>
        </div>
        
        {/* Step 3: Crop Image */}
        <div style={{ display: activeTab === 3 ? 'block' : 'none' }}>
             <div className={styles.cardBody}>
                <h3><FaCut /> Crop Your Image</h3>
                <h4>Drag and zoom to get the perfect fit.</h4>
                <div className={styles.cropperContainer}>
                    <Cropper image={uploadedImage} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onCropComplete={onCropComplete} onZoomChange={setZoom} cropShape={template.borderRadius === '50%' ? "round" : "rect"} showGrid={false} />
                </div>
                <div className={styles.zoomControlGroup}>
                    <label>Zoom</label>
                    <input type="range" className={styles.zoomSlider} value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(e.target.value)} />
                </div>
                <button className={`${styles.actionButton} ${styles.primary}`} style={{marginTop: '20px'}} onClick={handleCropSubmit}>Submit Crop</button>
            </div>
        </div>
        
        {/* Step 4: Final Image Display */}
        <div style={{ display: activeTab === 4 ? 'block' : 'none' }}>
            <div className={styles.cardBody}>
                 {canvasImage ? (
                    <>
                        <h3><FaCheckCircle style={{color: '#198754'}} /> Poster Ready!</h3>
                        <h4>Your user flow is correct: Download first, then share!</h4>
                        <img className={styles.finalImagePreview} src={canvasImage} alt="Generated Poster" />
                        <div className={styles.finalActionsContainer}>
                            <button className={`${styles.actionButton} ${styles.primary}`} onClick={downloadImage}><FaDownload/> 1. Download Image</button>
                            <button className={`${styles.actionButton} ${styles.whatsapp}`} onClick={handleWhatsAppShare}><FaWhatsapp/> 2. Share on WhatsApp</button>
                            <button className={`${styles.actionButton} ${styles.subtle}`} onClick={handleGenericShare}><FaShareAlt/> More Share Options</button>
                            <button className={`${styles.actionButton} ${styles.tertiary}`} onClick={() => window.location.reload()}><FaRedo/> Create Another</button>
                        </div>
                    </>
                 ) : (
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingSpinner}></div>
                      <h3>Generating your high-quality poster...</h3>
                    </div>
                 )}
            </div>
        </div>
      </div>

      {/* Hidden div for precise, high-quality rendering */}
      <div className={styles.hiddenRenderContainer} aria-hidden="true">
        <div className={styles.editorPreviewWrapper}>
          <div ref={finalImageRef} className={styles.mainImagePreview} style={{ height: `${template.imageHeight}px`, width: `${template.imageWidth}px` }}>
              <img className={styles.bgImage} src={template.imageUrl} alt="" />
              <div className={styles.dragContainer}>
                <div style={{
                    position: 'absolute',
                    width: `${layout.imageSize.width}px`,
                    height: `${layout.imageSize.height}px`,
                    transform: `translate(${layout.imagePosition.x}px, ${layout.imagePosition.y}px)`
                }}>
                    <img src={croppedImage} alt="" style={{ borderRadius: template.borderRadius, width: '100%', height: '100%' }}/>
                </div>
                <div style={{ 
                    position: 'absolute',
                    transform: `translate(${layout.textPosition.x}px, ${layout.textPosition.y}px)`,
                    fontFamily: template.fontFamily, 
                    color: template.fontColor, 
                    fontSize: `${layout.textSize}px`, 
                    fontWeight: template.fontWeight,
                    whiteSpace: 'nowrap' 
                }}>
                    {name}
                </div>
              </div>
          </div>
        </div>
        
      </div>
      
    </div>
  );
};

export default Imageshare;